/**
 * 群組樹主組件
 * 負責樹狀結構的整體渲染，使用 hooks 管理狀態
 */

import * as React from "react";
import { Tree, makeStyles, tokens } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import GroupTreeNode from "./GroupTree/GroupTreeNode";
import MemberNode from "./GroupTree/MemberNode";
import RecipientActions from "./GroupTree/RecipientActions";
import { useGroupData } from "../hooks/useGroupData";
import { useRecipients } from "../hooks/useRecipients";

const useStyles = makeStyles({
  root: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  treeContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: "6px",
    padding: "4px",
    border: `1px solid #B3D9F2`,
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
    "& [role='treeitem']": {
      paddingLeft: "8px !important",
      marginLeft: "0 !important",
    },
    "& [class*='fui-TreeItemLayout']": {
      paddingLeft: "0 !important",
      marginLeft: "0 !important",
    },
    // 不同層級的展開顏色
    "& [class*='fui-TreeItemLayout'][data-expanded='true'][data-level='0']": {
      backgroundColor: "#FAD7CE !important", 
      "&:hover": {
        backgroundColor: "#F1B7A6 !important",
      }
    },
    "& [class*='fui-TreeItemLayout'][data-expanded='true'][data-level='1']": {
      backgroundColor: "#FFE5CC !important",
      "&:hover": {
        backgroundColor: "#FFD1A6 !important",
      }
    },
    "& [class*='fui-TreeItemLayout'][data-expanded='true'][data-level='2']": {
      backgroundColor: "#FCF9E3 !important",
      "&:hover": {
        backgroundColor: "#F5EFC4 !important",
      }
    },
    "& [class*='fui-TreeItemLayout'][data-expanded='true'][data-level='3']": {
      backgroundColor: "#E1EFE4 !important",
      "&:hover": {
        backgroundColor: "#C8E0D1 !important",
      }
    },
    "& [class*='fui-TreeItemLayout'][data-expanded='true'][data-level='4']": {
      backgroundColor: "#D6EAF8 !important", 
      "&:hover": {
        backgroundColor: "#B3D7F0 !important", 
      }
    },
    "& [class*='fui-TreeItemLayout'][data-expanded='true'][data-level='5']": {
      backgroundColor: "#FAE6E0 !important",
      "&:hover": {
        backgroundColor: "#F1CFC2 !important",
      }
    },
    "& [class*='fui-TreeItemLayout'][data-expanded='true'][data-level='6']": {
      backgroundColor: "#FFE5CC !important",
      "&:hover": {
        backgroundColor: "#FFD1A6 !important",
      }
    },
    "& [class*='fui-TreeItemLayout'][data-expanded='true'][data-level='7']": {
      backgroundColor: "#FCF9E3 !important",
      "&:hover": {
        backgroundColor: "#F5EFC4 !important",
      }
    },
    "& [class*='fui-TreeItemLayout'][data-expanded='true'][data-level='8']": {
      backgroundColor: "#E1EFE4 !important",
      "&:hover": {
        backgroundColor: "#C8E0D1 !important",
      }
    },
    "& [class*='fui-TreeItemLayout'][data-expanded='true'][data-level='9']": {
      backgroundColor: "#D6EAF8 !important", 
      "&:hover": {
        backgroundColor: "#B3D7F0 !important", 
      }
    }
  },
  emptyMessage: {
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
    fontStyle: "italic",
    padding: "8px 0",
  },
});

const GroupTree = ({ groups, onAddRecipients, getAccessToken }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [expandedGroups, setExpandedGroups] = React.useState(new Set());
  
  // 使用 hooks
  const { groupMembers, groupSubgroups, loadingGroups, fetchMembers } = useGroupData(getAccessToken, { current: null });
  const { selectedGroups, selectedMembers, toggleGroup, toggleMember, clearSelection, hasSelection } = useRecipients();

  /**
   * 處理展開/收合
   */
  const handleToggleExpand = React.useCallback((nodePath, shouldExpand, groupId, deptId) => {
    const newExpanded = new Set(expandedGroups);
    if (shouldExpand) {
      newExpanded.add(nodePath);
      // 獲取資料
      if (groupId && deptId) {
        fetchMembers(groupId, deptId, nodePath);
      }
    } else {
      newExpanded.delete(nodePath);
    }
    setExpandedGroups(newExpanded);
  }, [expandedGroups, fetchMembers]);

  /**
   * 遞歸查找所有已載入的群組（包括子群組）
   */
  const findAllGroups = React.useCallback((groups, groupSubgroups, parentPath = 'root') => {
    const allGroups = [];
    
    groups.forEach(group => {
      const nodePath = `${parentPath}-${group.id}`;
      allGroups.push(group);
      
      // 遞歸查找子群組
      const subgroups = groupSubgroups[nodePath] || [];
      if (subgroups.length > 0) {
        const childGroups = findAllGroups(subgroups, groupSubgroups, nodePath);
        allGroups.push(...childGroups);
      }
    });
    
    return allGroups;
  }, []);

  /**
   * 處理新增收件人或出席者
   */
  const handleAddClick = React.useCallback(async (messageType) => {
    const memberToAdd = [];
    Object.values(groupMembers).flat().forEach(m => {
      if (selectedMembers.has(m.id)) {
        memberToAdd.push(m);
      }
    });

    // 收集所有已載入的群組（包括子群組）
    const allGroups = findAllGroups(groups, groupSubgroups);
    
    // 找到選中的群組物件（包括子群組）
    const selectedGroupItems = allGroups.filter(g => selectedGroups.has(g.id));

    try {
      onAddRecipients(selectedGroupItems, memberToAdd, messageType);
    }finally {
      clearSelection();
    }

  }, [groupMembers, selectedMembers, selectedGroups, groups, groupSubgroups, findAllGroups, onAddRecipients, clearSelection]);

  /**
   * 遞歸渲染樹節點
   */
  const renderTreeItem = React.useCallback((group, isParentExpanded = false, level = 0, parentPath = 'root') => {
    const nodePath = `${parentPath}-${group.id}`;
    const loadedSubgroups = groupSubgroups[nodePath] || [];
    const members = groupMembers[nodePath] || [];
    const isLoading = loadingGroups.has(nodePath);
    const isExpanded = expandedGroups.has(nodePath);
    const isSelected = selectedGroups.has(group.id);

    return (
      <GroupTreeNode
        key={nodePath}
        group={group}
        isSelected={isSelected}
        isExpanded={isExpanded}
        isParentExpanded={isParentExpanded}
        level={level}
        nodePath={nodePath}
        loadedSubgroups={loadedSubgroups}
        members={members}
        isLoading={isLoading}
        onToggleExpand={(path, shouldExpand) => {
          const deptId = group.displayName.split('.')[1];
          handleToggleExpand(path, shouldExpand, group.id, deptId);
        }}
        onToggleSelect={toggleGroup}
        onToggleMember={toggleMember}
        selectedMembers={selectedMembers}
        renderChildNode={(child, parentExpanded, childLevel, childParentPath) => 
          renderTreeItem(child, parentExpanded, childLevel, childParentPath)
        }
      />
    );
  }, [groupSubgroups, groupMembers, loadingGroups, expandedGroups, selectedGroups, handleToggleExpand, toggleGroup, toggleMember]);

  return (
    <div className={styles.root}>
      {groups.length === 0 ? (
        <div className={styles.emptyMessage}>未找到群組</div>
      ) : (
        <div className={styles.treeContainer}>
          <Tree aria-label="Group Tree">
            {groups.map((group) => renderTreeItem(group))}
          </Tree>
        </div>
      )}
      
      <RecipientActions
        hasSelection={hasSelection()}
        onAddTo={() => handleAddClick("to")}
        onAddCc={() => handleAddClick("cc")}
        onAddBcc={() => handleAddClick("bcc")}
        onAddRequiredAttendees={() => handleAddClick("requiredAttendees")}
        onAddOptionalAttendees={() => handleAddClick("optionalAttendees")}
        onClear={clearSelection}
      />
    </div>
  );
};

export default GroupTree;
